import { round } from 'lodash';
import { mapDegreesCardinal } from '@surfline/web-common';

import type {
  CamForecastConditions,
  CamForecastConditionsSwell,
  CameraConditionsUnits,
} from 'types/camera';
import conditionIsFlat from 'utils/conditionIsFlat';
import formatWaveHeight from 'utils/formatWaveHeight';
import { roundSurfHeight } from 'utils/roundUnits';

export function camMatchTimestampSeconds(localTimestamp: string): number | null {
  const ms = new Date(localTimestamp).getTime();
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
}

export function formatSurfHeightRange(
  surf: CamForecastConditions['surf'] | undefined,
  waveHeightUnit: CameraConditionsUnits['waveHeight'],
): string {
  if (!surf) return '—';
  if (conditionIsFlat(surf.max, waveHeightUnit)) return 'Flat';
  const min = roundSurfHeight(surf.min, waveHeightUnit);
  const max = roundSurfHeight(surf.max, waveHeightUnit);
  return `${min}-${max}`;
}

export function formatSurfHeightUnit(
  surf: CamForecastConditions['surf'] | undefined,
  waveHeightUnit: CameraConditionsUnits['waveHeight'],
): string {
  if (!surf || conditionIsFlat(surf.max, waveHeightUnit)) return '';
  return formatWaveHeight(waveHeightUnit, true);
}

export function formatPowerKj(power: number | undefined): string {
  if (power == null || !Number.isFinite(power)) return '—';
  return `${Math.round(power)}kJ`;
}

/** Signed relative power delta: positive when forecast > cam, negative when forecast < cam. */
export function formatKjDeltaPct(camPower: number, forecastPower: number): number | null {
  if (!Number.isFinite(camPower) || !Number.isFinite(forecastPower)) return null;
  const max = Math.max(camPower, forecastPower, 1);
  return Math.round(((forecastPower - camPower) / max) * 100);
}

export function formatSwellHeight(
  height: number,
  waveHeightUnit: CameraConditionsUnits['waveHeight'],
): string {
  const display = height >= 10 ? round(height, 0) : round(height, 1);
  return `${display}${formatWaveHeight(waveHeightUnit, true)}`;
}

export function formatSwellDirection(swell: CamForecastConditionsSwell): {
  cardinal: string;
  degrees: string;
} {
  return {
    cardinal: mapDegreesCardinal(swell.direction),
    degrees: `${round(swell.direction)}°`,
  };
}

export function formatWindSpeed(speed: number | undefined): string {
  if (speed == null || !Number.isFinite(speed)) return '—';
  return String(round(speed));
}

export function formatWindUnit(windSpeedUnit: CameraConditionsUnits['windSpeed']): string {
  return windSpeedUnit.toLowerCase();
}

export function formatWindDirectionLabel(wind: CamForecastConditions['wind'] | undefined): string {
  if (!wind?.direction) return '';
  return mapDegreesCardinal(wind.direction);
}

export function formatWindSubtext(
  wind: CamForecastConditions['wind'] | undefined,
  windSpeedUnit: CameraConditionsUnits['windSpeed'],
): string {
  if (!wind) return '';
  const unit = formatWindUnit(windSpeedUnit);
  const gustPart =
    wind.gust != null && Number.isFinite(wind.gust) ? `${round(wind.gust)}${unit} gust` : '';
  const directionPart = wind.directionType?.toLowerCase() ?? '';
  if (gustPart && directionPart) return `${gustPart}, ${directionPart}`;
  return gustPart || directionPart;
}

export function formatTideHeight(
  tide: CamForecastConditions['tide'] | undefined,
  tideHeightUnit: CameraConditionsUnits['tideHeight'],
): string {
  if (tide?.height == null || !Number.isFinite(tide.height)) return '—';
  const unit = tideHeightUnit === 'M' ? 'm' : 'ft';
  const display = tide.height >= 10 ? round(tide.height, 0) : round(tide.height, 1);
  return `${display}${unit}`;
}

export function formatTideTrend(
  trend: CamForecastConditions['tide']['trend'] | undefined,
): 'rising' | 'falling' | undefined {
  if (trend === 'RISING') return 'rising';
  if (trend === 'FALLING') return 'falling';
  return undefined;
}
